<template>
  <div class="pa-2">
    <v-card outlined flat>
      <v-card-text class="pt-0 mt-0">
        <v-subheader>{{ $t("message.filters") }}</v-subheader>
        <v-row v-if="availableLabels">
          <v-col>
            <tags-combo v-model="labelFilter" :label="$t('message.label')" :items="availableLabels"></tags-combo>
          </v-col>
          <v-col>
            <BeltsSelect v-model="beltFilter" :hide-details="true"></BeltsSelect>
          </v-col>
          <v-col>
            <v-select clearable @change="searchUsers()" hide-details v-model="genderFilter" :items="genderOptions"
              :label="$t('message.gender')" dense outlined>
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableSports" chips v-model="sportFilter" :label="$t('message.sport')" dense outlined>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableLevels" chips v-model="levelFilter" :label="$t('message.level')" dense outlined>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-text-field clearable @change="searchUsers();" hide-details type="number" min="0"
              v-model="ageFilter" :label="$t('message.age')" dense outlined>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-autocomplete @change="searchUsers(); getAvailableValues()" clearable hide-details small-chips
              :loading="loadingLists" :items="availableKeys" chips v-model="keyFilter"
              :label="$t('message.keys_filter')" dense outlined>
            </v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete clearable @change="searchUsers();" hide-details small-chips :loading="loadingLists"
              :items="availableValues" chips v-model="valueFilter" :label="$t('message.values_filter')" dense outlined>
            </v-autocomplete>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card v-if="loading" class="mt-2" flat outlined>
      <v-skeleton-loader type="list-item-avatar, list-item-avatar, list-item-avatar"></v-skeleton-loader>
    </v-card>
    <v-card v-else class="mt-2" flat outlined>
      <v-list>
        <v-subheader class="d-flex align-center">
          <v-checkbox class="mt-0 pt-0 mr-2" hide-details :input-value="allSelected"
            :indeterminate="selected.length > 0 && !allSelected" @change="toggleSelectAll"></v-checkbox>
          <span>
            {{ $t("message.total") }}: {{ users.length }}<span v-if="hasMore || users.length < total"> / {{ total
            }}</span>
          </span>
          <span v-if="selected.length" class="ml-2 text-caption">
            ({{ $t('message.members_selected', [selected.length]) }})
          </span>
          <v-spacer></v-spacer>
          <v-menu offset-y :disabled="selected.length == 0">
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" :disabled="selected.length == 0" outlined small elevation="0" color="primary">
                {{ $t("message.actions") }}
              </v-btn>
            </template>
            <v-list class="font-roboto text-body-1">
              <v-list-item>
                <v-list-item-title class="clickable" @click="openSendEmailDialog()">
                  {{ $t("message.send_emails") }}
                </v-list-item-title>
              </v-list-item>
              <v-list-item>
                <v-list-item-title class="clickable" @click="openSendNotiDialog()">
                  {{ $t("message.send_notifications") }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-subheader>
        <v-list-item v-for="(u, index) in users" :key="'user_' + index + '_' + u._id">
          <v-list-item-action class="mr-0">
            <v-checkbox hide-details class="mt-0 pt-0" :input-value="isSelected(u)"
              @change="toggleOne(u)"></v-checkbox>
          </v-list-item-action>
          <v-list-item-action class="clickable" @click="goToUserPage(u)">
            <v-icon v-if="u.type == 'visitor'">mdi-target-account</v-icon>
            <v-icon v-else-if="u.type == 'prospect'">mdi-file-document-edit-outline</v-icon>
            <v-icon color="primary" v-else>mdi-account-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content class="text-left clickable" @click="goToUserPage(u)">
            <v-list-item-title>{{ u.name }}<span v-if="u.age !== null && u.age !== undefined"
                class="text-caption ml-2">({{ u.age }})</span></v-list-item-title>
            <v-list-item-subtitle v-if="u.joinedDate">{{ $t("message.joined") }}: {{ $d(new Date(u.joinedDate),
              "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-else-if="u.firstVisit">{{ $t("message.first_visit") }}: {{ $d(new
              Date(u.firstVisit), "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-else-if="u.created">{{ $t("message.added") }}: {{ $d(new Date(u.created),
              "month_day_year", $i18n.locale) }}</v-list-item-subtitle>
            <v-list-item-subtitle v-if="u.key && u.keyValue">
              <v-chip small><span class="font-weight-black mr-1">{{ u.key }}:</span> <span>{{ u.keyValue
              }}</span></v-chip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="u.labels">
              <v-chip small v-for="(label, labelIndex) in u.labels" :key="'user_' + index + '_label_' + labelIndex">{{
                label }}</v-chip>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-avatar v-if="u.image">
            <img :src="userImg(u.image)" />
          </v-list-item-avatar>
        </v-list-item>
      </v-list>
      <v-dialog v-model="sendEmailDialog" max-width="90%" scrollable>
        <v-card outlined class="text-xs-left">
          <v-card-title>{{ $t('message.send_emails') }}</v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-2">
              {{ $t('message.members_selected', [emailRecipientCount]) }}
              <span v-if="noEmailCount" class="ml-1 text-caption">
                - {{ $t('message.advanced_search_no_email', [noEmailCount]) }}
              </span>
            </div>
            <v-text-field outlined dense :label="$t('message.subject')" v-model="subject"></v-text-field>
            <VueEditor :style="'background-color:white;color:black'" v-model="editor"
              :editor-toolbar="editorToolbar" />
          </v-card-text>
          <v-card-actions>
            <v-btn elevation="0" :disabled="sending" @click="sendEmailDialog = false">{{ $t('message.cancel')
            }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn outlined elevation="0" color="primary" :loading="sending"
              :disabled="!subject || !editor || emailRecipientCount == 0" @click="sendEmails()">
              {{ $t('message.send') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="sendNotiDialog" max-width="700">
        <v-card outlined class="text-xs-left">
          <v-card-title>{{ $t('message.send_notifications') }}</v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-2">
              {{ $t('message.members_selected', [notifyRecipientCount]) }}
            </div>
            <v-alert v-if="noAccountCount" dense text type="info" class="text-body-2">
              {{ $t('message.advanced_search_no_account', [noAccountCount]) }}
            </v-alert>
            <v-text-field outlined dense :label="$t('message.subject')" v-model="subject"></v-text-field>
            <v-textarea outlined rows="4" hide-details :label="$t('message.contents')"
              v-model="notificationContents"></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-btn elevation="0" :disabled="sending" @click="sendNotiDialog = false">{{ $t('message.cancel')
            }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn outlined elevation="0" color="primary" :loading="sending"
              :disabled="!subject || !notificationContents || notifyRecipientCount == 0" @click="sendNotifications()">
              {{ $t('message.send') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-card-actions v-if="hasMore" class="justify-center pb-3">
        <v-btn outlined small elevation="0" color="primary" :loading="loadingMore" @click="loadMore()">
          {{ $t('message.view_more') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import BeltsSelect from "@/components/inputs/BeltsSelect.vue";
import TagsCombo from "@/components/inputs/TagsCombo.vue";
import Api from "@/services/Api";
import { VueEditor } from "vue2-editor";

export default {
  data() {
    return {
      loading: true,
      loadingLists: true,
      selected: [],
      sendEmailDialog: false,
      sendNotiDialog: false,
      sending: false,
      subject: null,
      editor: null,
      notificationContents: null,
      editorToolbar: this.getEditorToolbar(),
      users: [],
      total: 0,
      hasMore: false,
      skip: 0,
      loadingMore: false,
      availableLevels: [],
      availableSports: [],
      availableKeys: [],
      availableValues: [],
      availableLabels: null,
      keyFilter: null,
      valueFilter: null,
      sportFilter: null,
      levelFilter: null,
      labelFilter: null,
      beltFilter: null,
      genderFilter: null,
      ageFilter: null,
      genderOptions: [
        { text: this.$t('message.male'), value: 'male' },
        { text: this.$t('message.female'), value: 'female' },
      ]
    }
  },
  components: {
    TagsCombo,
    BeltsSelect,
    VueEditor
  },
  computed: {
    allSelected() {
      return this.users.length > 0 && this.selected.length === this.users.length;
    },
    //A row only reaches the email endpoints through its member data record, so
    //anything without one cannot be mailed.
    emailRecipientCount() {
      return this.selected.filter((u) => u.memberDataId).length;
    },
    noEmailCount() {
      return this.selected.length - this.emailRecipientCount;
    },
    //Notifications land in the app, so they need a real BJJLINK account.
    //Visitors and inquiries added by an admin often have none.
    notifyRecipientCount() {
      return this.selected.filter((u) => u.userId).length;
    },
    noAccountCount() {
      return this.selected.length - this.notifyRecipientCount;
    },
  },
  created() {
    this.getAcademyLists();
    this.searchUsers();
    this.getAvailableLabels();
  },
  watch: {
    labelFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
    beltFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
    genderFilter: {
      handler: function () {
        this.searchUsers();
      },
    },
  },
  methods: {
    isSelected(u) {
      return this.selected.some((s) => s.memberDataId === u.memberDataId);
    },
    toggleOne(u) {
      if (this.isSelected(u))
        this.selected = this.selected.filter(
          (s) => s.memberDataId !== u.memberDataId
        );
      else this.selected.push(u);
    },
    toggleSelectAll() {
      if (this.allSelected) this.selected = [];
      else this.selected = this.users.slice();
    },
    openSendEmailDialog() {
      if (!this.selected.length) return;
      this.subject = null;
      this.editor = null;
      this.sendEmailDialog = true;
    },
    openSendNotiDialog() {
      if (!this.selected.length) return;
      this.subject = null;
      this.notificationContents = null;
      this.sendNotiDialog = true;
    },
    clearSelection() {
      this.selected = [];
      this.subject = null;
      this.editor = null;
      this.notificationContents = null;
    },
    //Advanced Search returns members, visitors and inquiries together, and the
    //two of them take different endpoints: members go through the user based
    //one so they keep alt emails and the BJJLINK footer, everybody else goes
    //through the member data one, which is the only way to reach somebody who
    //has no BJJLINK account.
    async sendEmails() {
      const withAccount = this.selected.filter((u) => u.userId);
      const withoutAccount = this.selected.filter(
        (u) => !u.userId && u.memberDataId
      );
      if (!withAccount.length && !withoutAccount.length) return;
      this.sending = true;
      const managedAcademy = this.$store.state.managedAcademy;
      try {
        if (withAccount.length) {
          await Api().post(
            "admins/v2/academy/" + managedAcademy + "/send/emails",
            {
              recipients: withAccount.map((u) => u.userId),
              subject: this.subject,
              contents: this.editor,
            }
          );
        }
        if (withoutAccount.length) {
          await Api().post(
            "admins/v2/academy/" + managedAcademy + "/prospects/send/emails",
            {
              recipients: withoutAccount.map((u) => u.memberDataId),
              subject: this.subject,
              contents: this.editor,
            }
          );
        }
        this.sending = false;
        this.sendEmailDialog = false;
        this.clearSelection();
        this.showAlert("success", "Oss!", ":)");
      } catch (e) {
        this.sending = false;
        this.showAlert("error", "Oops!", this.$t("message.error_generic"));
      }
    },
    sendNotifications() {
      const recipients = this.selected
        .filter((u) => u.userId)
        .map((u) => u.userId);
      if (!recipients.length) return;
      this.sending = true;
      Api()
        .post(
          "admins/academy/" +
            this.$store.state.selectedAcademy +
            "/send/notifications",
          {
            recipients: recipients,
            subject: this.subject,
            contents: this.notificationContents,
          }
        )
        .then(() => {
          this.sending = false;
          this.sendNotiDialog = false;
          this.clearSelection();
          this.showAlert("success", "Oss!", ":)");
        })
        .catch(() => {
          this.sending = false;
          this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    getAvailableLabels() {
      Api()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/tags")
        .then((response) => {
          if (response.data) {
            this.availableLabels = response.data.sort();
          }
        })
        .catch((e) => {
          this.loading = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    buildParams(skip = 0) {
      const params = { skip };
      if (this.keyFilter) params.key = this.keyFilter;
      if (this.valueFilter) params.value = this.valueFilter;
      if (this.sportFilter) params.sport = this.sportFilter;
      if (this.levelFilter) params.level = this.levelFilter;
      if (this.beltFilter) params.belt = this.beltFilter;
      if (this.labelFilter && this.labelFilter.length > 0) params.labels = this.labelFilter;
      if (this.genderFilter) params.gender = this.genderFilter;
      //Only send a real number - "" or a cleared box means no age filter.
      if (this.ageFilter !== null && this.ageFilter !== "" && !isNaN(parseInt(this.ageFilter, 10)))
        params.age = parseInt(this.ageFilter, 10);
      return params;
    },
    searchUsers() {
      this.skip = 0;
      this.loading = true;
      Api()
        .post(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/users/search", this.buildParams(0)
        )
        .then((response) => {
          if (response && response.data) {
            this.users = response.data.users;
            this.total = response.data.total;
            this.hasMore = response.data.hasMore;
            this.loading = false;
          }
        });
    },
    loadMore() {
      this.loadingMore = true;
      const nextSkip = this.skip + 100;
      Api()
        .post(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/users/search", this.buildParams(nextSkip)
        )
        .then((response) => {
          if (response && response.data) {
            this.users = this.users.concat(response.data.users);
            this.total = response.data.total;
            this.hasMore = response.data.hasMore;
            this.skip = nextSkip;
            this.loadingMore = false;
          }
        })
        .catch(() => { this.loadingMore = false; });
    },
    getAvailableValues() {
      if (!this.keyFilter) {
        this.getAcademyLists();
      } else {
        Api()
          .get(
            "admins/managedacademy/" +
            this.$store.state.managedAcademy +
            "/key-values/" + this.keyFilter + "/values"
          )
          .then((response) => {
            if (response && response.data) {
              this.availableValues = response.data.values;
              this.loadingLists = false;
            }
          });
      }
    },
    getAcademyLists() {
      Api()
        .get(
          "admins/managedacademy/" +
          this.$store.state.managedAcademy +
          "/lists"
        )
        .then((response) => {
          if (response && response.data) {
            this.availableLevels = response.data.levels;
            this.availableSports = response.data.sports;
            this.availableKeys = response.data.keys;
            this.availableValues = response.data.values;
            this.loadingLists = false;
          }
        });
    },
    goToUserPage(u) {
      if (u.type == 'member')
        this.navigateTo({ name: "viewmember", params: { memberid: u.userId } });
      else
        if (u.type == 'member')
          this.navigateTo({
            name: 'viewvisitor',
            params: { visitorId: u.memberDataId },
            crumbName: u.displayName || u.email,
          })
    }
  },
};
</script>
